import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { deviceAPI } from '@/services/api';
import type { Device } from '@/types/domain';

interface DeviceLog {
  id: number;
  action: string;
  details: string | null;
  created_at: string;
}

interface DeviceLogsDialogProps {
  open: boolean;
  device: Device | null;
  onClose: () => void;
}

/**
 * 格式化日志日期时间
 */
function formatLogDateTime(
  value: string,
  t: (key: string) => string
): string {
  try {
    const normalized = value.includes(' ') ? value.replace(' ', 'T') : value;
    const date = new Date(normalized);
    if (Number.isNaN(date.getTime())) {
      return t('devices.invalidDate') || 'Invalid Date';
    }
    return format(date, 'yyyy-MM-dd HH:mm:ss');
  } catch {
    return t('devices.invalidDate') || 'Invalid Date';
  }
}

/**
 * 设备日志对话框组件
 */
export default function DeviceLogsDialog({
  open,
  device,
  onClose,
}: DeviceLogsDialogProps) {
  const { t } = useTranslation();
  const [logs, setLogs] = useState<DeviceLog[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && device) {
      setLoading(true);
      deviceAPI
        .getLogs(device.id)
        .then((response) => {
          setLogs(response.data || []);
        })
        .catch((err) => {
          console.error('Failed to load device logs:', err);
          setLogs([]);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      // 对话框关闭时清空日志
      setLogs([]);
    }
  }, [open, device]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {t('devices.logsTitle', { id: device?.id })}
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : logs.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t('devices.noLogs')}
          </Typography>
        ) : (
          <List>
            {logs.map((log) => (
              <ListItem key={log.id} divider>
                <ListItemText
                  primary={log.action}
                  secondary={
                    <>
                      <Typography variant="caption" display="block">
                        {formatLogDateTime(log.created_at, t)}
                      </Typography>
                      {log.details && (
                        <Typography variant="caption" color="text.secondary">
                          {log.details}
                        </Typography>
                      )}
                    </>
                  }
                />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('devices.close')}</Button>
      </DialogActions>
    </Dialog>
  );
}
